import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { api } from '../lib/api';

/**
 * Sign-in via Nextcloud Login Flow v2.
 *
 * The user opens Nextcloud in a normal browser view, signs in there, and comes
 * back. We poll in the background until that happens. Two details matter:
 *
 *  - the login page opens in a new tab, because an installed iOS PWA that
 *    navigates away from its own scope loses the standalone context and can
 *    strand the user in a plain Safari window
 *  - polling keeps running while the app is backgrounded on iOS only
 *    intermittently, so we also re-check on visibilitychange
 */
export function LoginPage() {
  const { refresh } = useAuth();
  const [state, setState] = useState<'idle' | 'waiting' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [loginUrl, setLoginUrl] = useState('');

  const flowState = useRef<string | null>(null);
  const pollTimer = useRef<number | null>(null);
  const deadline = useRef<number>(0);

  const stopPolling = useCallback(() => {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (!flowState.current) return;

    if (Date.now() > deadline.current) {
      stopPolling();
      setState('error');
      setMessage('Sign-in timed out. Please try again.');
      return;
    }

    try {
      const result = await api.pollLogin(flowState.current);

      if (result.status === 'complete') {
        stopPolling();
        await refresh();
        return;
      }
    } catch {
      // A single failed poll is not fatal — the network may just have blipped.
    }

    pollTimer.current = window.setTimeout(() => void poll(), 2000);
  }, [refresh, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  /** iOS often freezes timers in a background tab; re-check on return. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && flowState.current) {
        void poll();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [poll]);

  const startLogin = useCallback(async () => {
    setState('waiting');
    setMessage('');

    try {
      const flow = await api.startLogin();
      flowState.current = flow.state;
      setLoginUrl(flow.loginUrl);
      deadline.current = Date.now() + 10 * 60 * 1000;

      // Opened from the click handler so Safari does not treat it as a popup.
      window.open(flow.loginUrl, '_blank', 'noopener,noreferrer');

      void poll();
    } catch (error) {
      setState('error');
      setMessage(
        error instanceof Error
          ? error.message
          : 'Could not reach the family server. Check your connection.',
      );
    }
  }, [poll]);

  return (
    <div className="app-shell">
      <main className="app-main">
        <div className="page" style={{ paddingTop: 'calc(var(--safe-top) + 48px)' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div
              style={{
                width: 76,
                height: 76,
                borderRadius: 20,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'grid',
                placeItems: 'center',
                margin: '0 auto 18px',
                fontSize: 34,
              }}
              aria-hidden="true"
            >
              ✦
            </div>
            <h1 style={{ margin: '0 0 8px', fontSize: '1.6rem', letterSpacing: '-0.02em' }}>
              Family Album
            </h1>
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.95rem' }}>
              Photos and messages, just for us.
            </p>
          </div>

          {state === 'error' && (
            <div className="banner error">
              <h3>Sign-in failed</h3>
              <p>{message}</p>
            </div>
          )}

          {state === 'waiting' ? (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div className="spinner" />
                <strong style={{ fontSize: '0.97rem' }}>Waiting for you to sign in…</strong>
              </div>
              <p>
                A Nextcloud sign-in page should have opened in another tab. Sign in there and
                tap <strong>Grant access</strong>, then come back here — this screen will
                continue on its own.
              </p>
              {loginUrl && (
                <a
                  className="btn btn-block"
                  href={loginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginTop: 4 }}
                >
                  Open the sign-in page again
                </a>
              )}
              <button
                type="button"
                className="btn btn-block"
                style={{ marginTop: 8, background: 'transparent' }}
                onClick={() => {
                  stopPolling();
                  flowState.current = null;
                  setState('idle');
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <>
              <button type="button" className="btn btn-primary btn-block" onClick={() => void startLogin()}>
                Sign in with Nextcloud
              </button>
              <p
                style={{
                  color: 'var(--text-faint)',
                  fontSize: '0.85rem',
                  textAlign: 'center',
                  marginTop: 16,
                }}
              >
                Use the same username and password you use for the family cloud. There is no
                separate account to remember.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
